/**
 * Phase 0 (docs/mobile-design.md §9 Q9): the phone layout is automatic on small touch screens; "Desktop site" in Me
 * switches it off on this device and is remembered (localStorage). The rule itself is shared (pickLayout).
 */
import { create } from 'zustand';
import { LAYOUT_PREF_KEY, MOBILE_MAX_WIDTH, parseLayoutPref, pickLayout, type LayoutPref } from '@tradeoutlet/shared';
import { useMedia } from '@/lib/useMedia';

const read = (): LayoutPref => { try { return parseLayoutPref(localStorage.getItem(LAYOUT_PREF_KEY)); } catch { return 'auto'; } };

export const useLayoutPref = create<{ pref: LayoutPref; setPref: (p: LayoutPref) => void }>(set => ({
  pref: read(),
  setPref: pref => {
    try { if (pref === 'auto') localStorage.removeItem(LAYOUT_PREF_KEY); else localStorage.setItem(LAYOUT_PREF_KEY, pref); } catch { /* private mode */ }
    set({ pref });
  },
}));

/** Small touch screen, whatever the preference (for offering the switch back). */
export function useIsPhone() {
  const narrow = useMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
  const coarse = useMedia('(pointer: coarse)');
  return pickLayout({ width: narrow ? MOBILE_MAX_WIDTH : MOBILE_MAX_WIDTH + 1, coarse, pref: 'auto' }) === 'mobile';
}

/** True when the mobile shell should render. */
export function useMobileLayout() {
  const phone = useIsPhone();
  const pref = useLayoutPref(s => s.pref);
  return phone && pref !== 'desktop';
}
